import { Link } from "react-router-dom";
import { FileText, Shield, ArrowRight, Zap, Lock, Clock, ClipboardList, ArrowRightLeft, Type, MessageSquareOff, Mail, QrCode, Receipt, FileSpreadsheet, ImageDown, Code2, FilePlus, Combine, Calculator, Coffee, Mic, Database } from "lucide-react";
import { Button } from "../components/ui/button";
import AdUnit from "../components/AdUnit";

const heroTools = [
  {
    path: "/tracker-generator",
    icon: ClipboardList,
    color: "#7C3AED",
    bgColor: "#EDE9FE",
    title: "Requirements Tracker Generator",
    description: "Paste your checklist or requirements and get a structured, color-coded Excel tracker sheet with priorities and status columns.",
    tags: ["Excel", "Tracker", "Requirements"],
    featured: true,
  },
  {
    path: "/qr-generator",
    icon: QrCode,
    color: "#0055FF",
    bgColor: "#EFF6FF",
    title: "QR Code Generator",
    description: "Create custom QR codes for URLs, WiFi, contact info. Customize colors and download instantly.",
    tags: ["QR", "Barcode", "Free"],
  },
  {
    path: "/invoice-generator",
    icon: Receipt,
    color: "#059669",
    bgColor: "#ECFDF5",
    title: "Invoice Generator",
    description: "Generate professional PDF invoices for freelancers and small businesses. No sign-up required.",
    tags: ["Invoice", "PDF", "Business"],
  },
  {
    path: "/pdf-to-excel",
    icon: FileSpreadsheet,
    color: "#DC2626",
    bgColor: "#FEE2E2",
    title: "PDF to Excel Converter",
    description: "Extract tables and text from PDF files into Excel spreadsheets automatically.",
    tags: ["PDF", "Excel", "Convert"],
  },
  {
    path: "/image-compressor",
    icon: ImageDown,
    color: "#7C3AED",
    bgColor: "#F3E8FF",
    title: "Image Compressor",
    description: "Reduce image file size up to 80% while maintaining quality. JPG, PNG, WebP supported.",
    tags: ["Image", "Compress", "Optimize"],
  },
  {
    path: "/json-formatter",
    icon: Code2,
    color: "#0284C7",
    bgColor: "#F0F9FF",
    title: "JSON Formatter",
    description: "Format, validate, and beautify JSON data. View structure statistics instantly.",
    tags: ["JSON", "Developer", "Format"],
  },
  {
    path: "/docx-to-pdf",
    icon: FilePlus,
    color: "#2563EB",
    bgColor: "#DBEAFE",
    title: "DOCX to PDF Converter",
    description: "Convert Microsoft Word documents to PDF format instantly. Fast and secure.",
    tags: ["DOCX", "PDF", "Convert"],
  },
  {
    path: "/pdf-merger",
    icon: Combine,
    color: "#DC2626",
    bgColor: "#FEE2E2",
    title: "PDF Merger",
    description: "Combine multiple PDF files into one document. Reorder pages easily.",
    tags: ["PDF", "Merge", "Combine"],
  },
  {
    path: "/json-to-excel",
    icon: ArrowRightLeft,
    color: "#10B981",
    bgColor: "#ECFDF5",
    title: "JSON to Excel",
    description: "Convert JSON data to a formatted Excel spreadsheet. No AI, pure instant conversion.",
    tags: ["JSON", "Excel", "Convert"],
  },
  {
    path: "/metadata-cleaner",
    icon: Shield,
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    title: "File Metadata Cleaner",
    description: "Remove hidden data from images, PDFs, and documents. Protect your privacy before sharing.",
    tags: ["Privacy", "EXIF", "Security"],
  },
  {
    path: "/formula-explainer",
    icon: Calculator,
    color: "#16A34A",
    bgColor: "#F0FDF4",
    title: "Excel Formula Explainer",
    description: "Paste any Excel formula and get a plain-English explanation of what it does.",
    tags: ["Excel", "Formula", "AI"],
  },
  {
    path: "/case-fixer",
    icon: Type,
    color: "#EA580C",
    bgColor: "#FFF7ED",
    title: "Case Fixer",
    description: "Fix text capitalization instantly. Smart mode, title case, sentence case, and more.",
    tags: ["Text", "Format", "AI"],
  },
  {
    path: "/leave-generator",
    icon: Mail,
    color: "#0891B2",
    bgColor: "#ECFEFF",
    title: "Leave Application Generator",
    description: "Generate professional leave application emails in seconds. Multiple tones available.",
    tags: ["Email", "HR", "AI"],
  },
  {
    path: "/excuse-generator",
    icon: Coffee,
    color: "#DB2777",
    bgColor: "#FCE7F3",
    title: "Meeting Excuse Generator",
    description: "Generate realistic, specific excuses to skip meetings. Professional and believable.",
    tags: ["Meeting", "Fun", "AI"],
  },
  {
    path: "/meeting-minutes",
    icon: Mic,
    color: "#7C3AED",
    bgColor: "#F5F3FF",
    title: "Meeting Minutes Generator",
    description: "Upload audio recordings and get structured meeting minutes with action items.",
    tags: ["Meeting", "Audio", "AI"],
  },
  {
    path: "/php-to-excel",
    icon: Database,
    color: "#8B5CF6",
    bgColor: "#F5F3FF",
    title: "PHP Unserialize to Excel",
    description: "The only tool with recursive unserialization. Convert nested PHP data to Excel with JSON preview.",
    tags: ["PHP", "Excel", "Unique"],
    badge: "One of a Kind",
  },
];

const features = [
  { icon: Zap, title: "Instant Results", desc: "No waiting. Get results in seconds." },
  { icon: Lock, title: "No Sign-Up", desc: "Use any tool without creating an account." },
  { icon: Clock, title: "Always Free", desc: "Core tools are free, forever." },
];

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-[#F8FAFC]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0s', animationFillMode: 'forwards' }}>
              <span className="label-mono inline-block mb-6 bg-white px-3 py-1.5 rounded-full border border-slate-200">
                17 Free Productivity Tools
              </span>
            </div>
            <h1
              className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.9] text-slate-900 animate-fade-in-up opacity-0"
              style={{ fontFamily: 'Chivo, sans-serif', animationDelay: '0.1s', animationFillMode: 'forwards' }}
              data-testid="hero-title"
            >
              Paste. Click.<br />
              <span className="text-[#0055FF]">Done.</span>
            </h1>
            <p
              className="mt-8 text-base md:text-lg leading-relaxed text-slate-600 max-w-xl animate-fade-in-up opacity-0"
              style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
            >
              Generate invoices, create QR codes, compress images, format JSON, convert PDFs — all free, no sign-up needed.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 animate-fade-in-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
              <Link to="/qr-generator">
                <Button className="bg-[#0055FF] hover:bg-[#0044DD] text-white rounded-md px-6 h-12 text-base font-medium btn-press" data-testid="hero-cta-qr">
                  Generate QR Code
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/invoice-generator">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press" data-testid="hero-cta-invoice">
                  Create Invoice
                </Button>
              </Link>
              <Link to="/image-compressor">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Compress Image
                </Button>
              </Link>
              <Link to="/pdf-merger">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Merge PDFs
                </Button>
              </Link>
              <Link to="/json-formatter">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Format JSON
                </Button>
              </Link>
              <Link to="/tracker-generator">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Generate Tracker
                </Button>
              </Link>
              <Link to="/pdf-to-excel">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  PDF to Excel
                </Button>
              </Link>
              <Link to="/docx-to-pdf">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  DOCX to PDF
                </Button>
              </Link>
              <Link to="/json-to-excel">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  JSON to Excel
                </Button>
              </Link>
              <Link to="/metadata-cleaner">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Clean Metadata
                </Button>
              </Link>
              <Link to="/formula-explainer">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Explain Formula
                </Button>
              </Link>
              <Link to="/case-fixer">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Fix Case
                </Button>
              </Link>
              <Link to="/leave-generator">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Leave Application
                </Button>
              </Link>
              <Link to="/excuse-generator">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Meeting Excuse
                </Button>
              </Link>
              <Link to="/meeting-minutes">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-slate-300 text-slate-700 hover:bg-slate-50 btn-press">
                  Meeting Minutes
                </Button>
              </Link>
              <Link to="/php-to-excel">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base font-medium border-purple-400 text-purple-700 hover:bg-purple-50 btn-press relative">
                  <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[9px] font-bold rounded-full uppercase">New</span>
                  PHP to Excel
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Ad */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <AdUnit format="leaderboard" />
      </div>

      {/* Tools Grid */}
      <section className="py-16 md:py-24" data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <span className="label-mono block mb-3">All Tools</span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              All tools, zero friction
            </h2>
          </div>

          {/* Featured tool (QR Generator) */}
          <Link
            to="/qr-generator"
            className="tool-card block border-2 border-[#0055FF] rounded-lg p-6 md:p-8 bg-white mb-6 animate-fade-in-up opacity-0"
            style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}
            data-testid="tool-card-qr-generator"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-14 h-14 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
                <QrCode className="w-7 h-7 text-[#0055FF]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    QR Code Generator
                  </h3>
                  <span className="text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0055FF] text-white">Popular</span>
                </div>
                <p className="text-sm text-slate-500 max-w-xl">
                  Create custom QR codes for URLs, WiFi networks, contact info, and more. Customize colors, size, and error correction. Download instantly.
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-[#0055FF] font-medium text-sm flex-shrink-0">
                Try Now <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>

          {/* Other tools grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {heroTools.filter(t => !t.featured).map((tool, index) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.path}
                  to={tool.path}
                  className="tool-card block border border-slate-200 rounded-lg p-6 bg-white animate-fade-in-up opacity-0"
                  style={{ animationDelay: `${0.1 * (index + 2)}s`, animationFillMode: 'forwards' }}
                  data-testid={`tool-card-${tool.path.slice(1)}`}
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: tool.bgColor }}>
                    <Icon className="w-5 h-5" style={{ color: tool.color }} />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{tool.title}</h3>
                    {tool.badge && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                        {tool.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed mb-5">{tool.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {tool.tags.map((tag) => (
                      <span key={tag} className="text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: tool.color }}>
                    Use Tool <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-[#F8FAFC]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {features.map((feat) => {
              const Icon = feat.icon;
              return (
                <div key={feat.title} className="flex gap-4">
                  <div className="w-10 h-10 rounded-md bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#0055FF]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{feat.title}</h3>
                    <p className="text-sm text-slate-500">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom Ad */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdUnit format="horizontal" />
      </div>
    </div>
  );
}
